// 畫布：渲染、平移縮放、點選、拖放、行內文字編輯、關聯線模式。
// 一個隱藏的 textarea 常駐在選取主題上並保持焦點：直接打字（含中文輸入法）就會進入編輯。
import { layoutMap, renderSvg, svgFrame, getTheme, type FontSet, type LayoutNode, type MapLayout, type Point } from '@sind/core';
import type { EditorState } from './state.js';

const PADDING = 40;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 3;
const DRAG_THRESHOLD = 5;

export type DropPosition = 'child' | 'before' | 'after';

export interface CanvasHooks {
  onEditCommit(id: string, title: string, then: 'none' | 'child' | 'sibling'): void;
  onMove(id: string, target: string, position: DropPosition): void;
  onRelate(from: string, to: string): void;
  onToggle(id: string): void;
  /** 畫布未在編輯文字時的按鍵；回傳 true 表示已處理 */
  onKey(event: KeyboardEvent): boolean;
  onZoom(zoom: number): void;
  /** 進入／離開播放模式 */
  onPlayChange?(playing: boolean): void;
}

export class Canvas {
  layout!: MapLayout;
  zoom = 1;
  private tx = 0;
  private ty = 0;
  private frame = { width: 0, height: 0, offsetX: 0, offsetY: 0 };
  private nodes = new Map<string, LayoutNode>();
  private readonly world: HTMLDivElement;
  private readonly mapLayer: HTMLDivElement;
  private readonly overlay: SVGSVGElement;
  readonly input: HTMLTextAreaElement;
  private editing: string | null = null;
  private relationFrom: string | null = null;
  private pointer: Point | null = null;
  private drag: { id: string; start: Point; active: boolean; target: { id: string; position: DropPosition } | null } | null = null;
  private pan: { start: Point; tx: number; ty: number } | null = null;
  private fitted = false;

  constructor(private readonly viewport: HTMLElement, private readonly state: EditorState, private readonly fonts: FontSet, private readonly hooks: CanvasHooks) {
    this.world = document.createElement('div');
    this.world.className = 'world';
    this.mapLayer = document.createElement('div');
    this.mapLayer.className = 'map-layer';
    this.overlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.overlay.classList.add('overlay');
    this.world.append(this.mapLayer, this.overlay);
    this.input = document.createElement('textarea');
    this.input.className = 'inline-editor';
    this.input.setAttribute('aria-label', 'Topic text');
    this.input.spellcheck = false;
    viewport.append(this.world, this.input);
    this.bindEvents();
  }

  // ---------- 座標換算 ----------

  /** 排版座標 → 視窗座標（相對 viewport 左上角） */
  toScreen(point: Point): Point {
    return { x: this.tx + (point.x + this.frame.offsetX) * this.zoom, y: this.ty + (point.y + this.frame.offsetY) * this.zoom };
  }

  /** 視窗座標 → 排版座標 */
  toLayout(point: Point): Point {
    return { x: (point.x - this.tx) / this.zoom - this.frame.offsetX, y: (point.y - this.ty) / this.zoom - this.frame.offsetY };
  }

  private local(event: { clientX: number; clientY: number }): Point {
    const rect = this.viewport.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  node(id: string): LayoutNode | undefined {
    return this.nodes.get(id);
  }

  // ---------- 渲染 ----------

  render(): void {
    this.stopPlay();
    const rootBefore = this.layout ? this.nodes.get(this.layout.nodes[0]!.id) : undefined;
    const anchor = rootBefore ? this.toScreen(center(rootBefore)) : null;
    this.layout = layoutMap(this.state.map, this.fonts);
    this.nodes = new Map(this.layout.nodes.map(node => [node.id, node]));
    this.frame = svgFrame(this.layout, PADDING);
    this.mapLayer.innerHTML = renderSvg(this.layout, this.fonts, { interactive: true, padding: PADDING });
    this.overlay.setAttribute('width', String(this.frame.width));
    this.overlay.setAttribute('height', String(this.frame.height));
    this.viewport.style.background = getTheme(this.state.map.theme).background;
    this.viewport.classList.toggle('dark', this.state.map.theme === 'sketch-dark');
    if (!this.fitted) { this.fit(); this.fitted = true; }
    else if (anchor) {
      // 以中心主題為錨點，新增節點時畫面不跳動。
      const rootAfter = this.nodes.get(this.layout.nodes[0]!.id)!;
      const now = this.toScreen(center(rootAfter));
      this.tx += anchor.x - now.x;
      this.ty += anchor.y - now.y;
    }
    this.applyTransform();
    this.drawOverlay();
  }

  private applyTransform(): void {
    this.world.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.zoom})`;
    this.placeInput();
  }

  fit(): void {
    const width = this.viewport.clientWidth || 800;
    const height = this.viewport.clientHeight || 600;
    this.zoom = clamp(Math.min(width / this.frame.width, height / this.frame.height) * 0.92, MIN_ZOOM, 1.4);
    this.tx = (width - this.frame.width * this.zoom) / 2;
    this.ty = (height - this.frame.height * this.zoom) / 2;
    this.applyTransform();
    this.drawOverlay();
    this.hooks.onZoom(this.zoom);
  }

  zoomBy(factor: number, at?: Point): void {
    const focus = at ?? { x: this.viewport.clientWidth / 2, y: this.viewport.clientHeight / 2 };
    const before = this.toLayout(focus);
    this.zoom = clamp(this.zoom * factor, MIN_ZOOM, MAX_ZOOM);
    const after = this.toScreen(before);
    this.tx += focus.x - after.x;
    this.ty += focus.y - after.y;
    this.applyTransform();
    this.hooks.onZoom(this.zoom);
  }

  // 選取的主題不在畫面內時，平移到可見。
  reveal(id: string): void {
    const node = this.nodes.get(id);
    if (!node) return;
    const margin = 40;
    const topLeft = this.toScreen({ x: node.x, y: node.y });
    const bottomRight = this.toScreen({ x: node.x + node.w, y: node.y + node.h });
    const width = this.viewport.clientWidth;
    const height = this.viewport.clientHeight;
    if (topLeft.x < margin) this.tx += margin - topLeft.x;
    else if (bottomRight.x > width - margin) this.tx -= bottomRight.x - (width - margin);
    if (topLeft.y < margin) this.ty += margin - topLeft.y;
    else if (bottomRight.y > height - margin) this.ty -= bottomRight.y - (height - margin);
    this.applyTransform();
  }

  // 覆蓋層：選取框、關聯線預覽、拖放提示。
  drawOverlay(): void {
    const parts: string[] = [];
    const { offsetX, offsetY } = this.frame;
    const selection = this.state.selection;
    const accent = 'var(--accent)';
    if (selection?.kind === 'topic') {
      const node = this.nodes.get(selection.id);
      if (node) parts.push(`<rect x="${node.x + offsetX - 5}" y="${node.y + offsetY - 5}" width="${node.w + 10}" height="${node.h + 10}" rx="10" fill="none" stroke="${accent}" stroke-width="2.5"/>`);
    } else if (selection?.kind === 'relation') {
      const relation = this.layout.relations.find(item => item.from === selection.from && item.to === selection.to);
      if (relation) {
        const [a, b, c, d] = relation.points.map(point => ({ x: point.x + offsetX, y: point.y + offsetY }));
        parts.push(`<path d="M${a!.x} ${a!.y}C${b!.x} ${b!.y} ${c!.x} ${c!.y} ${d!.x} ${d!.y}" fill="none" stroke="${accent}" stroke-width="5" stroke-opacity="0.45" stroke-linecap="round"/>`);
      }
    }
    if (this.relationFrom && this.pointer) {
      const source = this.nodes.get(this.relationFrom);
      if (source) {
        const from = center(source);
        const to = this.toLayout(this.pointer);
        parts.push(`<line x1="${from.x + offsetX}" y1="${from.y + offsetY}" x2="${to.x + offsetX}" y2="${to.y + offsetY}" stroke="${accent}" stroke-width="2" stroke-dasharray="7 5"/>`);
      }
    }
    if (this.drag?.active && this.pointer) {
      const dragged = this.nodes.get(this.drag.id);
      const at = this.toLayout(this.pointer);
      if (dragged) parts.push(`<rect x="${at.x + offsetX - dragged.w / 2}" y="${at.y + offsetY - dragged.h / 2}" width="${dragged.w}" height="${dragged.h}" rx="8" fill="${accent}" fill-opacity="0.12" stroke="${accent}" stroke-dasharray="5 4"/>`);
      const target = this.drag.target ? this.nodes.get(this.drag.target.id) : undefined;
      if (target && this.drag.target) {
        const x = target.x + offsetX;
        const y = target.y + offsetY;
        if (this.drag.target.position === 'child') parts.push(`<rect x="${x - 4}" y="${y - 4}" width="${target.w + 8}" height="${target.h + 8}" rx="10" fill="${accent}" fill-opacity="0.15" stroke="${accent}" stroke-width="2"/>`);
        else if (target.side === 0) {
          const lineX = this.drag.target.position === 'before' ? x - 8 : x + target.w + 8;
          parts.push(`<line x1="${lineX}" y1="${y - 4}" x2="${lineX}" y2="${y + target.h + 4}" stroke="${accent}" stroke-width="4" stroke-linecap="round"/>`);
        } else {
          const lineY = this.drag.target.position === 'before' ? y - 6 : y + target.h + 6;
          parts.push(`<line x1="${x - 4}" y1="${lineY}" x2="${x + target.w + 4}" y2="${lineY}" stroke="${accent}" stroke-width="4" stroke-linecap="round"/>`);
        }
      }
    }
    this.overlay.innerHTML = parts.join('');
    this.placeInput();
  }

  // ---------- 行內編輯 ----------

  get isEditing(): boolean {
    return this.editing !== null;
  }

  focus(): void {
    if (document.activeElement !== this.input) this.input.focus({ preventScroll: true });
  }

  // 隱藏輸入框跟著選取的主題，讓輸入法候選窗出現在正確位置。
  private placeInput(): void {
    const id = this.editing ?? (this.state.selection?.kind === 'topic' ? this.state.selection.id : null);
    const node = id ? this.nodes.get(id) : undefined;
    if (!node) { this.input.style.transform = 'translate(-9999px, 0)'; return; }
    const topLeft = this.toScreen({ x: node.x, y: node.y });
    const fontSize = node.text.fontSize * this.zoom;
    const width = Math.max(node.w, 160) * this.zoom;
    this.input.style.transform = `translate(${topLeft.x}px, ${topLeft.y}px)`;
    this.input.style.width = `${width}px`;
    this.input.style.minHeight = `${node.h * this.zoom}px`;
    this.input.style.fontSize = `${fontSize}px`;
    this.input.style.padding = `${Math.max(2, (node.h - node.text.height) / 2) * this.zoom}px ${8 * this.zoom}px`;
  }

  startEdit(id: string, initial?: string): void {
    const topic = this.nodes.get(id)?.topic;
    if (!topic) return;
    this.editing = id;
    this.input.classList.add('editing');
    this.input.value = initial ?? topic.title;
    this.placeInput();
    this.focus();
    if (initial === undefined) this.input.select();
    else this.input.setSelectionRange(this.input.value.length, this.input.value.length);
  }

  private finishEdit(then: 'none' | 'child' | 'sibling', cancel = false): void {
    const id = this.editing;
    if (!id) return;
    this.editing = null;
    this.input.classList.remove('editing');
    const value = this.input.value.replace(/\s+/g, ' ').trim();
    this.input.value = '';
    if (!cancel && value) this.hooks.onEditCommit(id, value, then);
    else if (then !== 'none') this.hooks.onEditCommit(id, this.nodes.get(id)?.topic.title ?? value, then);
    this.placeInput();
  }

  // ---------- 播放動畫 ----------

  private player: HTMLImageElement | null = null;

  get isPlaying(): boolean {
    return this.player !== null;
  }

  /** 在目前位置與縮放就地播放動態 SVG（以 <img> 顯示，不受頁面 CSP 影響） */
  play(svg: string): void {
    this.stopPlay();
    const img = document.createElement('img');
    img.className = 'player';
    img.alt = '';
    img.width = this.frame.width;
    img.height = this.frame.height;
    img.src = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    this.world.append(img);
    this.player = img;
    this.viewport.classList.add('playing');
    this.hooks.onPlayChange?.(true);
  }

  stopPlay(): void {
    if (!this.player) return;
    URL.revokeObjectURL(this.player.src);
    this.player.remove();
    this.player = null;
    this.viewport.classList.remove('playing');
    this.hooks.onPlayChange?.(false);
  }

  // ---------- 關聯線模式 ----------

  startRelation(from: string): void {
    this.relationFrom = from;
    this.viewport.classList.add('picking');
  }

  cancelModes(): void {
    this.relationFrom = null;
    this.drag = null;
    this.viewport.classList.remove('picking', 'dragging');
    this.drawOverlay();
  }

  get isPicking(): boolean {
    return this.relationFrom !== null;
  }

  // ---------- 事件 ----------

  private hit(target: EventTarget | null): { node?: string; relation?: { from: string; to: string }; toggle?: string } {
    const element = target instanceof Element ? target : null;
    const toggle = element?.closest<SVGElement>('[data-toggle]')?.dataset.toggle;
    if (toggle) return { toggle };
    const relation = element?.closest<SVGElement>('[data-relation]')?.dataset.relation;
    if (relation) { const [from, to] = relation.split('>'); return { relation: { from: from!, to: to! } }; }
    const node = element?.closest<SVGElement>('[data-id]')?.dataset.id;
    return node ? { node } : {};
  }

  // 拖放目標：指標下方的主題（排除被拖曳的子樹），依位置決定成為子主題或前後兄弟。
  private dropTarget(point: Point, draggedId: string): { id: string; position: DropPosition } | null {
    const at = this.toLayout(point);
    const excluded = new Set<string>();
    const collect = (id: string) => { excluded.add(id); this.nodes.get(id)?.topic.children.forEach(childTopic => collect(childTopic.id)); };
    collect(draggedId);
    const target = this.layout.nodes.find(node => !excluded.has(node.id) && at.x >= node.x - 6 && at.x <= node.x + node.w + 6 && at.y >= node.y - 6 && at.y <= node.y + node.h + 6);
    if (!target) return null;
    if (target.depth === 0) return { id: target.id, position: 'child' };
    const ratio = target.side === 0 ? (at.x - target.x) / target.w : (at.y - target.y) / target.h;
    const position: DropPosition = ratio < 0.25 ? 'before' : ratio > 0.75 ? 'after' : 'child';
    return { id: target.id, position };
  }

  private bindEvents(): void {
    const viewport = this.viewport;

    viewport.addEventListener('pointerdown', event => {
      if (event.button !== 0 && event.button !== 1) return;
      if (event.target === this.input && this.editing) return;
      // 播放中點一下回到編輯。
      if (this.player) { this.stopPlay(); event.preventDefault(); this.focus(); return; }
      const point = this.local(event);
      const hit = this.hit(event.target);
      if (this.editing) this.finishEdit('none');
      if (this.relationFrom) {
        if (hit.node && hit.node !== this.relationFrom) this.hooks.onRelate(this.relationFrom, hit.node);
        this.cancelModes();
        event.preventDefault();
        this.focus();
        return;
      }
      if (hit.toggle) { this.hooks.onToggle(hit.toggle); event.preventDefault(); this.focus(); return; }
      if (hit.relation) { this.state.select({ kind: 'relation', ...hit.relation }); event.preventDefault(); this.focus(); return; }
      if (hit.node && event.button === 0) {
        if (this.state.selection?.kind !== 'topic' || this.state.selection.id !== hit.node) this.state.select({ kind: 'topic', id: hit.node });
        this.drag = { id: hit.node, start: point, active: false, target: null };
      } else {
        this.pan = { start: point, tx: this.tx, ty: this.ty };
        viewport.classList.add('panning');
      }
      viewport.setPointerCapture(event.pointerId);
      event.preventDefault();
      this.focus();
    });

    viewport.addEventListener('pointermove', event => {
      const point = this.local(event);
      this.pointer = point;
      if (this.pan) {
        this.tx = this.pan.tx + point.x - this.pan.start.x;
        this.ty = this.pan.ty + point.y - this.pan.start.y;
        this.applyTransform();
        return;
      }
      if (this.drag) {
        const moved = Math.hypot(point.x - this.drag.start.x, point.y - this.drag.start.y);
        const isRoot = this.nodes.get(this.drag.id)?.depth === 0;
        if (!this.drag.active && moved > DRAG_THRESHOLD && !isRoot) { this.drag.active = true; viewport.classList.add('dragging'); }
        if (this.drag.active) { this.drag.target = this.dropTarget(point, this.drag.id); this.drawOverlay(); }
        return;
      }
      if (this.relationFrom) this.drawOverlay();
    });

    const endPointer = (event: PointerEvent) => {
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      if (this.pan) { this.pan = null; viewport.classList.remove('panning'); }
      if (this.drag) {
        const { id, active, target } = this.drag;
        this.drag = null;
        viewport.classList.remove('dragging');
        if (active && target) this.hooks.onMove(id, target.id, target.position);
        this.drawOverlay();
      }
    };
    viewport.addEventListener('pointerup', endPointer);
    viewport.addEventListener('pointercancel', endPointer);

    viewport.addEventListener('dblclick', event => {
      const hit = this.hit(event.target);
      if (hit.node) this.startEdit(hit.node);
    });

    viewport.addEventListener('wheel', event => {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) this.zoomBy(Math.exp(-event.deltaY * 0.0015), this.local(event));
      else { this.tx -= event.deltaX; this.ty -= event.deltaY; this.applyTransform(); }
    }, { passive: false });

    // 輸入框：編輯中處理 Enter／Esc／Tab；未編輯時把按鍵交給快捷鍵，打字則進入編輯。
    let composing = false;
    this.input.addEventListener('compositionstart', () => {
      composing = true;
      const selection = this.state.selection;
      if (!this.editing && selection?.kind === 'topic') this.startEdit(selection.id, '');
    });
    this.input.addEventListener('compositionend', () => { composing = false; });
    // 沒有經過 keydown 的輸入（手機鍵盤、部分輸入法）也進入編輯，不吞字。
    this.input.addEventListener('input', () => {
      const selection = this.state.selection;
      if (!this.editing && !composing && selection?.kind === 'topic' && this.input.value) this.startEdit(selection.id, this.input.value);
    });
    this.input.addEventListener('keydown', event => {
      if (composing || event.isComposing) return;
      if (this.editing) {
        if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); this.finishEdit('none'); }
        else if (event.key === 'Tab') { event.preventDefault(); this.finishEdit('child'); }
        else if (event.key === 'Escape') { event.preventDefault(); this.finishEdit('none', true); }
        return;
      }
      if (event.key === 'Escape' && this.relationFrom) { event.preventDefault(); this.cancelModes(); return; }
      if (this.player) { event.preventDefault(); if (event.key === 'Escape' || event.key === ' ' || event.key === 'Enter') this.stopPlay(); return; }
      if (this.hooks.onKey(event)) { event.preventDefault(); return; }
      const printable = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
      const selection = this.state.selection;
      if (printable && selection?.kind === 'topic') { event.preventDefault(); this.startEdit(selection.id, event.key); }
    });
    this.input.addEventListener('blur', () => { if (this.editing) this.finishEdit('none'); });

    new ResizeObserver(() => this.placeInput()).observe(viewport);
  }
}

function center(node: LayoutNode): Point {
  return { x: node.x + node.w / 2, y: node.y + node.h / 2 };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
